import type { ChangeEvent, FC, KeyboardEvent } from "react"
import { useState, Fragment, useEffect, useId, useRef } from "react"

import type { FlattenSimpleInterpolation } from "styled-components"
import styled from "styled-components"

import { defaultTextInputStyles } from "../styles"
import { Wrapper } from "../utils/Wrapper"

export const PhoneNumberLayoutDiv = styled.div`
  display: flex;
  flex-direction: row;
  input {
    width: 100%;
    max-width: 6ch;
    font-feature-settings: "tnum";
    /* border: 1px solid #ccc; */
    flex-shrink: 1;
    &:last-of-type {
      max-width: none;
    }
  }
  [data-role="phone-number-formatting"] {
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    max-width: 1.4em;
  }
`
type PhoneChangeEventHandler = (
  event: ChangeEvent<HTMLInputElement>,
  segmentIdx: number,
) => void

type PhoneKeyDownEventHandler = (
  event: KeyboardEvent<HTMLInputElement>,
  segmentIdx: number,
) => void

export type PhoneProps = {
  disabled?: boolean
  label?: string
  phoneNumber: string | null
  setPhoneNumber: (phoneNumber: string) => void
  css?: FlattenSimpleInterpolation
}

export const Phone: FC<PhoneProps> = ({
  disabled,
  label,
  phoneNumber: possiblyNullPhoneNumber,
  setPhoneNumber,
  css = defaultTextInputStyles,
}) => {
  const phoneNumber = (possiblyNullPhoneNumber ?? ``).replace(/[^0-9]/g, ``)
  const [currentKeyboardEvent, setCurrentKeyboardEvent] = useState(
    null as KeyboardEvent<HTMLInputElement> | null,
  )

  const phoneNumberSegments = [
    {
      prefix: `+1 (`,
      ref: useRef<HTMLInputElement>(null),
      range: { start: 0, end: 3 },
    },
    {
      prefix: `) `,
      ref: useRef<HTMLInputElement>(null),
      range: { start: 3, end: 6 },
    },
    {
      prefix: ` - `,
      ref: useRef<HTMLInputElement>(null),
      range: { start: 6, end: Infinity },
    },
  ]

  const cursorPosition = useRef<[segmentIdx: number, cursorIdx: number]>([0, 0])
  useEffect(() => {
    if (phoneNumber.length > 0) {
      const [segmentIdx, cursorIdx] = cursorPosition.current
      const input = phoneNumberSegments[segmentIdx].ref.current
      if (input) {
        input.setSelectionRange(cursorIdx, cursorIdx)
      }
    }
  }, [phoneNumber])

  const onKeyDown: PhoneKeyDownEventHandler = (event, idx) => {
    const segment = phoneNumberSegments[idx]
    const prevSegment = phoneNumberSegments[idx - 1]
    const nextSegment = phoneNumberSegments[idx + 1]
    const { start, end } = segment.range
    const { current: input } = segment.ref
    const cursorIndex = input?.selectionStart ?? 0
    const inputIndex = start + cursorIndex
    const isAtStart = start === inputIndex
    const isAtEnd = end === inputIndex
    setCurrentKeyboardEvent(event)

    const didType = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9].includes(Number(event.key))
    if (didType) {
      const isOneFromEnd = end === inputIndex + 1
      cursorPosition.current = isOneFromEnd
        ? [idx + 1, 0]
        : isAtEnd
          ? [idx + 1, 1]
          : [idx, cursorIndex + 1]
    }
    if (event.key === `Backspace`) {
      const shouldJumpToPrev = isAtStart && prevSegment
      cursorPosition.current = shouldJumpToPrev
        ? [idx - 1, prevSegment.range.end - (prevSegment.range.start + 1)]
        : [idx, cursorIndex - 1]
    }
    if (event.key === `Delete`) {
      cursorPosition.current = [idx, cursorIndex]
    }

    switch (event.key) {
      case `Backspace`:
        if (isAtStart && prevSegment) {
          const { current: prevInput } = prevSegment.ref
          prevInput?.focus()
          prevInput?.setSelectionRange(
            prevSegment.range.end - 1,
            prevSegment.range.end - 1,
          )
        }
        break
      case `ArrowLeft`:
        if (isAtStart && prevSegment) {
          const { current: prevInput } = prevSegment.ref
          prevInput?.focus()
          const prevInputLength = prevInput?.value.length ?? 0
          prevInput?.setSelectionRange(prevInputLength, prevInputLength)
        }
        break
      case `ArrowRight`:
        if (isAtEnd && nextSegment) {
          const { current: nextInput } = nextSegment.ref
          nextInput?.focus()
          nextInput?.setSelectionRange(0, 0)
        }
        break
      case `ArrowUp`:
        phoneNumberSegments[0].ref.current?.focus()
        phoneNumberSegments[0].ref.current?.setSelectionRange(0, 0)
        break
      case `ArrowDown`:
        if (isAtEnd) {
          nextSegment?.ref.current?.focus()
        } else {
          segment.ref.current?.setSelectionRange(
            segment.range.end,
            segment.range.end,
          )
        }
        break
      case `0`:
      case `1`:
      case `2`:
      case `3`:
      case `4`:
      case `5`:
      case `6`:
      case `7`:
      case `8`:
      case `9`:
        if (isAtEnd && nextSegment) {
          const { current: nextInput } = nextSegment.ref
          nextInput?.focus()
          nextInput?.setSelectionRange(0, 0)
        }
        break
    }
  }

  const changePhoneNumber: PhoneChangeEventHandler = (e, idx) => {
    const segment = phoneNumberSegments[idx]
    const { start, end } = segment.range
    const filteredValue = e.target.value.replace(/[^0-9]/g, ``)
    if (phoneNumber.length >= 10 && currentKeyboardEvent?.key !== `Backspace`) {
      return
    }
    const newPhoneNumber =
      phoneNumber.substring(0, start) +
      filteredValue +
      phoneNumber.substring(end)
    setPhoneNumber(newPhoneNumber)
    const cursorIndex = e.target.selectionStart ?? 0
    const inputIndex = start + cursorIndex
    const prevSegment = phoneNumberSegments[idx - 1]
    const nextSegment = phoneNumberSegments[idx + 1]
    if (inputIndex === start && prevSegment) {
      const prevEnd = prevSegment.range.end
      prevSegment.ref.current?.focus()
      prevSegment.ref.current?.setSelectionRange(prevEnd, prevEnd)
    }
    if (inputIndex === end && nextSegment) {
      nextSegment.ref.current?.focus()
    }
  }

  const phoneNumberId = useId()

  return (
    <Wrapper css={css}>
      {label && <label htmlFor={label}>{label}</label>}
      <PhoneNumberLayoutDiv data-testid="phone" data-role="phone">
        {phoneNumberSegments.map(
          ({ ref, range: { start, end }, prefix }, idx) => (
            <Fragment key={phoneNumberId + `_` + idx}>
              <span data-role="phone-number-formatting">{prefix}</span>
              <input
                type="tel"
                disabled={disabled}
                ref={ref}
                data-testid={`phone-number-input` + `-` + idx}
                value={phoneNumber.substring(start, end)}
                onChange={(e) => changePhoneNumber(e, idx)}
                onKeyDown={(e) => onKeyDown(e, idx)}
              />
            </Fragment>
          ),
        )}
      </PhoneNumberLayoutDiv>
    </Wrapper>
  )
}
